import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CaseSubmission, FallbackFormData } from "./FallbackForm";

interface QuestionnaireFormProps {
  fallbackData: FallbackFormData;
  onSubmit: (submission: CaseSubmission) => void;
  onBack: () => void;
}

export function QuestionnaireForm({ fallbackData, onSubmit, onBack }: QuestionnaireFormProps) {
  const { t } = useTranslation();

  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const MAX_CHARS = 500;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setScreenshot(null);
      setScreenshotError(null);
      return;
    }

    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setScreenshotError(t("questionnaire_screenshot_error_type"));
      setScreenshot(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setScreenshotError(t("questionnaire_screenshot_error_size"));
      setScreenshot(null);
      return;
    }
    setScreenshot(file);
    setScreenshotError(null);
  };

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (description.trim().length < 10 || screenshotError !== null) {
      setShowError(true);
      return;
    }

    setShowError(false);

    onSubmit({
      ...fallbackData,
      description: description.trim(),
      screenshot,
    });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          {t("questionnaire_title")}
        </h1>
        <p className="text-gray-500 text-sm mb-6">{t("questionnaire_intro")}</p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Description */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("questionnaire_description")} *
              <textarea
                maxLength={MAX_CHARS}
                rows={4}
                placeholder={t("questionnaire_description_placeholder")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <span className="text-xs text-gray-400 text-right block">
                {t("questionnaire_chars_remaining", { count: MAX_CHARS - description.length })}
              </span>
            </label>
          </div>

          {/* Screenshot upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("questionnaire_screenshot")}
            </label>
            <p className="text-xs text-gray-400 mb-2">{t("questionnaire_screenshot_hint")}</p>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleFileChange}
              className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
            {screenshot && !screenshotError && (
              <p className="text-xs text-green-600 mt-1">
                {t("questionnaire_screenshot_selected", { name: screenshot.name })}
              </p>
            )}
            {screenshotError && (
              <p role="alert" className="text-xs text-red-600 mt-1">
                {screenshotError}
              </p>
            )}
          </div>

          {/* Validation error */}
          {showError && (
            <p role="alert" className="text-red-600 text-sm mb-4">
              {t("questionnaire_required_hint")}
            </p>
          )}

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <button
              type="submit"
              className="w-full py-3 px-4 btn-primary rounded-xl font-medium transition-colors"
            >
              {t("questionnaire_submit")}
            </button>
            <button
              type="button"
              onClick={onBack}
              className="w-full py-3 px-4 bg-white border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              {t("step_prev")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
